import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n2 } from "../../lib/format";
import { Modal, Notice } from "../../lib/ui";

/*
 * Update a fixed asset (update_asset) – built new, as the PHP page wasn't available. Admin only.
 * Opened from Fixed Assets with ?id=. Suggests the straight-line book value from cost, salvage value and useful life.
 */

const F = [["asset_name", "Asset name", "text", true], ["asset_type", "Asset type", "text"], ["purchase_date", "Purchase date", "date"], ["purchase_cost", "Purchase cost (TZS)", "number"],
  ["depreciation_method", "Depreciation method", "select"], ["useful_life_years", "Useful life (years)", "number"], ["salvage_value", "Salvage value (TZS)", "number"],
  ["current_value", "Current value (TZS)", "number"], ["location", "Location", "text"], ["status", "Status", "status"]];
const METHODS = ["Straight Line", "Declining Balance", "Units of Production", "None"];
const STATUSES = ["In Use", "Under Repair", "Idle", "Disposed"];

export default function UpdateAsset() {
  const api = useApi();
  const navigate = useNavigate();
  const [qs] = useSearchParams();
  const id = qs.get("id");
  const [f, setF] = useState(null);
  const [notice, setNotice] = useState(null);
  const [del, setDel] = useState(false);
  useEffect(() => {
    if (id) api.get(`/api/assets/${id}`).then((a) => setF(Object.fromEntries(F.map(([k]) => [k, a[k] == null ? "" : String(k === "purchase_date" ? String(a[k]).slice(0, 10) : a[k])]))))
      .catch((e) => setNotice({ tone: "err", text: e.message }));
  }, [api, id]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  // straight-line book value today, as a suggestion
  const suggested = (() => {
    if (!f || !f.purchase_date || !Number(f.useful_life_years)) return null;
    const years = (Date.now() - new Date(f.purchase_date + "T00:00:00")) / (365.25 * 864e5);
    const cost = Number(f.purchase_cost) || 0, salvage = Number(f.salvage_value) || 0;
    return Math.max(salvage, cost - ((cost - salvage) / Number(f.useful_life_years)) * Math.max(0, years));
  })();
  const save = async (e) => {
    e.preventDefault();
    try { const r = await api.request(`/api/assets/${id}`, "PUT", f); setNotice({ tone: "ok", text: r.message }); }
    catch (err) { setNotice({ tone: "err", text: err.message }); }
  };
  const remove = async () => {
    setDel(false);
    try { await api.request(`/api/assets/${id}`, "DELETE"); navigate("/assets", { replace: true }); }
    catch (e) { setNotice({ tone: "err", text: e.message }); }
  };
  return (
    <main className="pg" style={{ maxWidth: "56rem" }}>
      <PageHeader slug="update_asset" actions={<Link to="/assets" className="btn"><i className="fas fa-arrow-left" /> Fixed assets</Link>} />
      <Notice notice={notice} onClose={() => setNotice(null)} />
      {!id && <div className="panel empty">Open this page from <Link to="/assets">Fixed Assets</Link>.</div>}
      {f && (
        <form className="panel form" onSubmit={save} style={{ maxWidth: "none" }}>
          {F.map(([k, l, type, req]) => (
            <div key={k} className={`field ${k === "asset_name" ? "wide" : ""}`}><label htmlFor={`ua-${k}`}>{l}{req && <span className="req">*</span>}</label>
              {type === "select" ? <select id={`ua-${k}`} value={f[k]} onChange={set(k)}>{[...new Set([...METHODS, f[k]].filter(Boolean))].map((m) => <option key={m}>{m}</option>)}</select>
                : type === "status" ? <select id={`ua-${k}`} value={f[k]} onChange={set(k)}>{[...new Set([...STATUSES, f[k]].filter(Boolean))].map((m) => <option key={m}>{m}</option>)}</select>
                : <input id={`ua-${k}`} type={type} step={type === "number" ? "0.01" : undefined} min={type === "number" ? 0 : undefined} required={req} value={f[k]} onChange={set(k)} />}
              {k === "current_value" && suggested != null && <span className="hint">Straight-line value today: {n2(suggested)} <button type="button" className="lg-link" style={{ margin: 0, fontSize: ".8rem" }} onClick={() => setF({ ...f, current_value: suggested.toFixed(2) })}>Use this</button></span>}
            </div>))}
          <div className="form-actions" style={{ justifyContent: "space-between" }}>
            <button type="button" className="btn" onClick={() => setDel(true)}><i className="fas fa-trash-can" /> Delete asset</button>
            <button type="submit" className="btn primary"><i className="fas fa-floppy-disk" /> Save changes</button></div>
        </form>
      )}
      {del && <Modal title="Delete this asset?" onClose={() => setDel(false)} actions={<><button type="button" className="btn" onClick={() => setDel(false)}>Cancel</button><button type="button" className="btn danger" onClick={remove}>Delete</button></>}>
        <b>{f.asset_name}</b> will be removed from the asset register. If it was sold or scrapped, setting its status to Disposed keeps its history instead.</Modal>}
    </main>
  );
}
